import webApiAdmin from '@/lib/api-common/webApiAdmin';
import type { UserChannel } from '@/lib/utils/channelPlatformMeta';
import { clearCampaignCache, type PriceBreakdownItem } from '@/lib/api/campaign.api';

/** listHistory user.uncheckedAdvertiserActions 항목 (광고주 미확인 변경 알림) */
export interface UncheckedAdvertiserAction {
  _id?: string;
  id?: string;
  createdAt?: string;
  at?: string;
  message?: string;
  subAction?: string;
  field?: string;
  key?: string;
  value?: unknown;
  to?: unknown;
  newValue?: unknown;
  from?: unknown;
  previousValue?: unknown;
}

export interface HistoryRowUser {
  _id?: string;
  title?: string;
  thumbnail?: string;
  channels?: UserChannel[];
  /** Other linked platforms (icons only — original `renderTitle` used this for the
   *  small platform-icon row under the channel name, e.g. a YouTuber who also streams on Twitch). */
  socialChannels?: Array<{ type?: string; channelId?: string }>;
  // v2 BE returns raw `stats` (allTime + last-5 averages) and, for YouTube, a
  // per-kind `statsYoutubeBreakdown` — the display reads these as fallbacks for
  // the legacy `averageStats` / `averageStatsYoutubeBreakdown` names.
  stats?: {
    allTime?: { subscribers?: number; views?: number; posts?: number };
    last5?: { views?: number; likes?: number; comments?: number; retweets?: number; replies?: number; saves?: number };
  };
  statsYoutubeBreakdown?: {
    last5?: {
      videos?: { views?: number; likes?: number; comments?: number };
      shorts?: { views?: number; likes?: number; comments?: number };
      live?: { views?: number; likes?: number; comments?: number };
    };
  };
  averageStats?: {
    last5?: {
      views?: number;
      likes?: number;
      comments?: number;
      duration?: number;
      chatMessages?: number;
      retweets?: number;
      replies?: number;
      saves?: number;
    };
    last5Streams?: Array<{ views?: number; duration?: number; chatMessages?: number; publishedAt?: string; title?: string; url?: string }>;
    last5Posts?: Array<{ views?: number; publishedAt?: string; title?: string; url?: string }>;
  };
  bilibiliStats?: { views?: number; comments?: number; reviews?: number };
  chzzkStats?: { duration?: { avg?: number }; maxViewers?: { avg?: number }; avgViewers?: { avg?: number } };
  afreecaStats?: { duration?: { avg?: number }; maxViewers?: { avg?: number }; avgViewers?: { avg?: number } };
  country?: string;
  defaultReasonForRecommendation?: string;
  hashtags?: string[];
  lastVideo?: { publishedAt?: string; title?: string; url?: string };
  latestPost?: { publishedAt?: string; title?: string; url?: string };
  latestPostByType?: Record<string, { publishedAt?: string; title?: string; url?: string } | null>;
  twitchStats?: {
    lastStreamAt?: string;
    duration?: { avg?: number };
    maxViewers?: { avg?: number };
    avgViewers?: { avg?: number };
    chatMessages?: { avg?: number };
  };
  averageStatsYoutubeBreakdown?: {
    last5?: {
      videos?: { views?: number; likes?: number; comments?: number };
      shorts?: { views?: number; likes?: number; comments?: number };
      live?: { views?: number; likes?: number; comments?: number };
    };
  };
  recentGames?: Array<{ _id?: string; title?: string; name?: string; percent?: number; percentage?: number }>;
  advertiserApprovalCount?: number;
  uncheckedAdvertiserActions?: UncheckedAdvertiserAction[];
  unchecked_advertiser_actions?: UncheckedAdvertiserAction[];
  campaigns?: Array<{
    _id?: string;
    campaignId?: string;
    name?: string;
    price?: number;
    /** finalPrice, priceFinal, advertiserPrice 중 하나 (가격 변동 표시용) */
    finalPrice?: number;
    priceFinal?: number;
    advertiserPrice?: number;
    priceProposal?: number;
    status?: string;
    /** Partnership(광고 타입) - 툴팁 표시용 */
    videoType?: string;
    /** 정렬용 (최신순) */
    createdAt?: string;
    /** Vod 카운트/가격: links 중 vod 타입 (type, generalPostType, price) */
    links?: Array<{ postId?: string; type?: string; generalPostType?: string; price?: number }>;
    /** posts로 Vod 가격 계산 시 사용 (partnership=vod인 post의 price 합) */
    posts?: Array<{ partnership?: string; type?: string; price?: number }>;
    campaign?: { _id?: string; name?: string };
    /** 일부 listHistory 응답: 해당 캠페인 유저-캠페인 조인에 미확인 알림 배열 */
    uncheckedAdvertiserActions?: UncheckedAdvertiserAction[];
    unchecked_advertiser_actions?: UncheckedAdvertiserAction[];
  }>;
}

export interface HistoryRowStats {
  duration?: { min?: number; max?: number; avg?: number };
  maxViewers?: { min?: number; max?: number; avg?: number };
  avgViewers?: { min?: number; max?: number; avg?: number };
  views?: { min?: number; max?: number; avg?: number };
  viewsPerHour?: { min?: number; max?: number; avg?: number };
  lastStreamAt?: string;
}

/** `CampaignWorkOrderEvent.items[]` element (see gc-be CampaignEvent.model.ts). */
export interface WorkOrderItem {
  advertisingType?: string;
  /** Deadline dates; the UI reads the last entry. */
  publicationPeriod?: string[];
  /** Set for `advertisingType === 'stream'`. */
  startTime?: string;
  /** Set for `advertisingType === 'footage-right'`; free-text duration (e.g. "3 months"). */
  duration?: string;
}

/** Discriminated post-stats union — `data` shape follows `type`. */
export interface HistoryPostStats {
  type?: 'postStats' | 'streamStatsSum' | 'streamStatsSumAwareCampaignGame';
  data?: {
    views?: number;
    likes?: number;
    dislikes?: number;
    comments?: number;
    duration?: number;
    maximumViewers?: number;
    avgViewers?: number;
  };
}

export interface HistoryPost {
  _id?: string;
  title?: string;
  url?: string;
  systemId?: string;
  type?: string;
  /** Advertising type this post was submitted against (e.g. 'youtube_video', 'stream'). */
  partnership?: string;
  approvalStatus?: string;
  /** Admin added this post manually (not via URL crawl) — gc-mono's "Manual" tag. */
  isManual?: boolean;
  /** Placeholder/preview post with no real link yet — gc-mono's "Preview" tag. */
  isPreview?: boolean;
  createdAt?: string;
  /** Publish/live date for the confirmed post (streams: stream start; videos: upload date). */
  publishedAt?: string;
  submittedAt?: string;
  /** Admin-set date the video is expected to go live (youtube_video posts, waiting_for_release tab). */
  scheduledReleaseDate?: string;
  userId?: string;
  stats?: HistoryPostStats;
  rejectionFeedbacksHistory?: Array<{ date?: string; content?: string }>;
  /** 광고주 미확인 변경 — 이 포스트의 상태 변경/거절 피드백 등 (post 단위 알림) */
  uncheckedAdvertiserActions?: UncheckedAdvertiserAction[];
  advertiserApprovalCount?: number;
}

/** Referral link (`admin/communications/history/list` → `links[]`). */
export interface HistoryLink {
  _id?: string;
  url?: string;
  shortUrl?: string;
  sharableLink?: string;
  linkType?: string;
  postId?: string;
  comment?: string;
  createdAt?: string;
  clicks?: number;
}

/** Click-through demographic (`LinkStatsAllTime`) — counts, not percentages; the UI aggregates. */
export interface HistoryLinkStats {
  longUrlClicks?: number;
  shortUrlClicks?: number;
  countries?: Array<{ id: string; count: number }>;
  platforms?: Array<{ id: string; count: number }>;
}

export interface HistoryRow {
  id?: string | number;
  key?: string | number;
  userId?: number;
  title?: string;
  /** New BE exposes the channel thumbnail at row level (User.getThumbnail); legacy nested it under user.thumbnail */
  thumbnail?: string;
  channelType?: string;
  channelUrl?: string;
  subscribers?: number;
  country?: string;
  status?: string;
  comment?: string;
  priceFinal?: number;
  priceFootageRight?: number;
  priceSocialMediaPost?: number;
  priceItemReimbursement?: number;
  /** row 레벨 twitchStats (Twitch 채널인 경우 last5 평균 등) */
  stats?: HistoryRowStats;
  /** row 레벨 recentGames */
  recentGames?: Array<{ _id?: string; title?: string; name?: string; percent?: number; percentage?: number }>;
  /** row 레벨 hashtags */
  hashtags?: string[];
  /** row 레벨 latestPost */
  latestPost?: { publishedAt?: string; title?: string; url?: string; type?: string };
  posts?: HistoryPost[];
  submittedPosts?: HistoryPost[];
  user?: HistoryRowUser;
  workOrders?: Array<{
    _id?: string;
    /** Human-readable work order number, e.g. "20260624-54864641-040341". */
    uid?: string;
    /** Active statuses only — BE scopes this list to `new` | `accepted`. */
    status?: 'new' | 'accepted';
    createdAt?: string;
    pdfFileUrl?: string;
    items?: WorkOrderItem[];
  }>;
  lonelyWorkOrderItems?: Array<{ item?: WorkOrderItem }>;
  advertisingType?: string;
  /** completed 상태: 참조 링크 (숏 URL + 클릭수) */
  links?: HistoryLink[];
  /** completed 상태: 클릭 데모그라픽 (국가별 카운트) */
  demo?: HistoryLinkStats;
  costPerView?: number;
  costPerClick?: number;
  /** 스트리밍 가능 채널(Twitch/YouTube 등)에서만 존재 */
  costPerViewer?: number;
  candidateGroupName?: string;
  candidateManualEstimation?: Record<string, unknown>;
  candidateAutoEstimation?: Record<string, unknown>;
  /** 광고주 미확인 변경 (user와 동일 데이터가 row에만 올 수 있음) */
  uncheckedAdvertiserActions?: UncheckedAdvertiserAction[];
  unchecked_advertiser_actions?: UncheckedAdvertiserAction[];
  inclusionComment?: string;
  lastPrice?: number;
  lastPriceCampaignName?: string;
  priceBreakdown?: PriceBreakdownItem[];
  /** Set when a work order price exists — breakdown editing is disabled in that case */
  priceWorkOrder?: number;
}

/** POST /api3/communications/listHistory */
export async function listHistory(params: {
  campaignId: string;
  statusesToShow?: string[];
}): Promise<HistoryRow[]> {
  const { campaignId, statusesToShow } = params;
  if (!campaignId) return [];

  const payload: Record<string, unknown> = { campaignId };
  if (statusesToShow?.length) payload.statusesToShow = statusesToShow;

  const data = await webApiAdmin.post<HistoryRow[]>('admin/communications/history/list', payload);
  return Array.isArray(data) ? data : [];
}

export async function listCandidateHistory(params: {
  campaignId: string;
  statusesToShow: string[];
}): Promise<HistoryRow[]> {
  return listHistory({ campaignId: params.campaignId, statusesToShow: params.statusesToShow });
}

/**
 * Per-user candidate manual estimation (e.g. advertiserApprovalStatus, partnership).
 * POST /api3/communications/setCandidateManualEstimation
 *
 * NOTE: this is per-influencer (takes `userId`). The new BE
 * `admin/campaign/candidate-manual-estimation/set` is a different,
 * CAMPAIGN-level endpoint (only `views`/`budget`, no userId) — routing per-user
 * writes there 400s. This uses the per-user `admin/communications/...` endpoint,
 * which persists advertiserApprovalStatus/feedback per influencer.
 */
export async function setCandidateManualEstimation(params: {
  userId: number | string;
  campaignId: string;
  key: string;
  value: unknown;
}): Promise<void> {
  const { campaignId, key, userId, value } = params;
  if (!campaignId || !key) {
    return;
  }
  // v2 is per-user (like legacy) but takes a { keyAndValue: { key, value } }
  // discriminated union — the BE destructures keyAndValue.key / keyAndValue.value
  // (verified against admin-communications/setCandidateManualEstimation), NOT a
  // flat { [key]: value } map.
  await webApiAdmin.post('admin/communications/set-candidate-manual-estimation', {
    userId,
    campaignId,
    keyAndValue: { key, value },
  });
}

/**
 * Append advertiser rejection feedback to a rejected post.
 * POST /admin/communications/add-rejection-feedback
 */
export async function addRejectionFeedback(postId: string, feedback: string): Promise<void> {
  await webApiAdmin.post('admin/communications/add-rejection-feedback', { postId, feedback });
}

/* ── 인플루언서 상세 (더보기 패널) ── */

export interface SocialEntry {
  platform: string;
  url?: string;
  followers?: number;
}

export interface InfluencerPost {
  systemId?: string;
  type?: string;
  url?: string;
  title?: string;
  campaignName?: string;
  partnership?: string;
  publishedAt?: string;
}

export interface InfluencerDetails {
  _id?: string;
  title?: string;
  thumbnail?: string;
  country?: string;
  channels?: UserChannel[];
  /** 소셜 미디어 (key: "facebook_com", "instagram_com", ...) */
  socials?: Record<string, string>;
  youtubeSubscribers?: number;
  twitchFollowers?: number;
  tiktokFollowers?: number;
  facebookLikes?: number;
  instagramFollowers?: number;
  twitterFollowers?: number;
  douyinFollowers?: number;
  discordMembers?: number;
  discordOnlineMembers?: number;
  telegramMembers?: number;
  bilibiliFollowers?: number;
  chzzkFollowers?: number;
  afreecaFollowers?: number;
  /** 데모그라픽 */
  demographic?: { geographic?: Record<string, number> };
  profileDemographicV2?: { allTime?: { countries?: Array<{ id: string; count: number }> } };
  profileDemographic?: { allTime?: { countries?: Array<{ id: string; count: number }> } };
  /** 이전 캠페인 영상 */
  posts?: InfluencerPost[];
  latestPost?: InfluencerPost;
  sponsoredVideoComments?: Array<{ type?: string; systemId?: string; comment?: string }>;
}

/** POST /api3/communications/getInfluencerDetails */
export async function getInfluencerDetails(userId: string): Promise<InfluencerDetails | null> {
  if (!userId) return null;
  // New returns { user, posts }; legacy returned the user doc with posts
  // inlined. Flatten so consumers keep reading top-level fields + posts.
  const data = await webApiAdmin.post<{ user?: InfluencerDetails; posts?: InfluencerDetails['posts'] }>(
    'admin/communications/get-influencer-details',
    { userId },
  );
  if (!data?.user) return null;
  return { ...data.user, posts: data.posts ?? data.user.posts };
}

/**
 * POST /api3/communications/createCandidateGroup
 * Adds a new entry to campaign.candidateGroups — getCampaigns() caches campaigns for
 * INDEXED_CACHE_TTL_MS (5 min), so callers re-fetching right after this would otherwise
 * see the sidebar's group list miss the new group until the cache naturally expires.
 */
export async function createCandidateGroup(params: {
  campaignId: string;
  userIds: (string | number)[];
  groupName: string;
}): Promise<void> {
  await webApiAdmin.post('admin/communications/create-candidate-group', params);
  await clearCampaignCache();
}

/** POST /api3/communications/addToCandidateGroup */
export async function addToCandidateGroup(params: {
  campaignId: string;
  userIds: (string | number)[];
  groupName: string;
}): Promise<void> {
  await webApiAdmin.post('admin/communications/add-to-candidate-group', params);
}

/** POST /api3/communications/removeFromCandidateGroup */
export async function removeFromCandidateGroup(params: {
  campaignId: string;
  userIds: (string | number)[];
  groupName?: string;
}): Promise<void> {
  await webApiAdmin.post('admin/communications/remove-from-candidate-group', params);
}

/** POST /api3/communications/removeCandidateGroup — see createCandidateGroup for why this clears the campaigns cache. */
export async function removeCandidateGroup(params: {
  campaignId: string;
  groupName: string;
}): Promise<void> {
  await webApiAdmin.post('admin/communications/remove-candidate-group', params);
  await clearCampaignCache();
}

/** POST /api3/communications/reorderCandidateGroup — see createCandidateGroup for why this clears the campaigns cache. */
export async function reorderCandidateGroup(params: {
  campaignId: string;
  groupName: string;
  replacedGroupName: string;
}): Promise<void> {
  await webApiAdmin.post('admin/communications/reorder-candidate-group', params);
  await clearCampaignCache();
}

/** POST /api3/communications/renameCandidateGroup — see createCandidateGroup for why this clears the campaigns cache. */
export async function renameCandidateGroup(params: {
  campaignId: string;
  groupName: string;
  newGroupName: string;
}): Promise<void> {
  await webApiAdmin.post('admin/communications/rename-candidate-group', params);
  await clearCampaignCache();
}
